import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import GlossaryTermHighlight from './GlossaryTermHighlight';

/**
 * Resolves the display type for a line based on is_stage_direction value.
 */
const getLineDisplayType = (line) => {
  const lineType = line?.is_stage_direction || line?.isStageDirection;
  
  if (lineType === 'STAGE_DIRECTION') return 'stage_direction';
  if (lineType === 'CHARACTER') return 'character_header';
  if (lineType === 'LINE') return 'dialogue';
  
  if (lineType === true || line?.stageDirection) return 'stage_direction';
  if (line?.character) return 'dialogue';
  
  return 'dialogue';
};

/**
 * Splits dialogue text into segments, wrapping first-occurrence glossary terms
 * with GlossaryTermHighlight. Returns an array of React nodes.
 */
const renderHighlightedText = (text, glossaryTerms, isDark) => {
  if (!text || !glossaryTerms?.length) return text;

  // Sort terms longest-first so multi-word terms match before substrings
  const sorted = [...glossaryTerms]?.sort((a, b) => (b?.word?.length || 0) - (a?.word?.length || 0));

  // Build a single regex that matches any term (whole-word, case-insensitive)
  // Escape special regex chars in each term
  const escaped = sorted?.map(t => t?.word?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`\\b(${escaped.join('|')})\\b`, 'gi');

  const segments = [];
  let lastIndex = 0;
  let match;
  const usedTerms = new Set(); // track first-occurrence per line

  // Reset lastIndex
  pattern.lastIndex = 0;

  while ((match = pattern?.exec(text)) !== null) {
    const matchedText = match?.[0];
    const lowerMatch = matchedText?.toLowerCase();

    // Find the glossary term object (case-insensitive)
    const termObj = sorted?.find(t => t?.word?.toLowerCase() === lowerMatch);
    if (!termObj) continue;

    // Only highlight first occurrence per term per line
    if (usedTerms?.has(lowerMatch)) continue;
    usedTerms?.add(lowerMatch);

    // Push text before this match
    if (match?.index > lastIndex) {
      segments?.push(text?.slice(lastIndex, match?.index));
    }

    // Push highlighted term
    segments?.push(
      <GlossaryTermHighlight key={`${lowerMatch}-${match?.index}`} term={termObj} isDark={isDark}>
        {matchedText}
      </GlossaryTermHighlight>
    );

    lastIndex = match?.index + matchedText?.length;
  }

  // Push remaining text
  if (lastIndex < text?.length) {
    segments?.push(text?.slice(lastIndex));
  }

  return segments?.length > 0 ? segments : text;
};

const ScriptLine = ({ line, onLineClick, isHighlighted, isBookmarked, isDark, lineRef, glossaryTerms }) => {
  const displayType = getLineDisplayType(line);

  // Memoize highlighted content for dialogue lines
  const highlightedDialogue = useMemo(() => {
    if (displayType !== 'dialogue') return null;
    return renderHighlightedText(line?.dialogue, glossaryTerms, isDark);
  }, [line?.dialogue, glossaryTerms, isDark, displayType]);

  // STAGE_DIRECTION
  if (displayType === 'stage_direction') {
    return (
      <div
        ref={lineRef}
        className={`py-2 px-4 rounded transition-colors ${
          isHighlighted
            ? 'bg-accent/20' : isDark ? 'hover:bg-gray-800/60' : 'hover:bg-muted/40'
        }`}
      >
        <p className={`text-xs italic tracking-wide leading-6 ${
          isDark ? 'text-gray-400' : 'text-muted-foreground/70'
        }`}>
          {line?.stageDirection || line?.dialogue}
        </p>
      </div>
    );
  }

  // CHARACTER header
  if (displayType === 'character_header') {
    return (
      <div ref={lineRef} className="pt-6 pb-1 px-4">
        <span className="font-extrabold text-primary text-sm uppercase tracking-widest">
          {line?.dialogue || line?.character || ''}
        </span>
      </div>
    );
  }

  // Dialogue — clickable with glossary highlights
  return (
    <div
      ref={lineRef}
      className={`relative py-2 pl-8 pr-4 rounded cursor-pointer transition-all duration-200 ${
        isBookmarked
          ? isDark
            ? 'bg-amber-950/30 border-l-4 border-amber-400' : 'bg-amber-50/70 border-l-4 border-amber-400'
          : isHighlighted
          ? isDark
            ? 'bg-primary/20 border-l-2 border-primary' : 'bg-primary/10 border-l-2 border-primary'
          : isDark
          ? 'hover:bg-gray-800/60' : 'hover:bg-muted/40'
      }`}
      onClick={() => onLineClick?.(line)}
    >
      {/* Bookmark badge */}
      {isBookmarked && (
        <span
          className="absolute top-2 right-2 flex items-center gap-0.5 text-[10px] font-semibold text-amber-500 bg-amber-100/80 dark:bg-amber-900/60 px-1.5 py-0.5 rounded-full"
          title="Bookmarked"
        >
          <Icon name="Bookmark" size={10} className="fill-amber-400 text-amber-500" />
          saved
        </span>
      )}
      <p className={`text-base leading-8 tracking-normal ${
        isDark ? 'text-gray-100' : 'text-foreground'
      }`}>
        {highlightedDialogue}
      </p>
      {line?.hasAudio && (
        <span className={`inline-flex items-center gap-1 text-xs mt-1 ${
          isDark ? 'text-amber-400' : 'text-secondary'
        }`}>
          <span>🎧</span>
        </span>
      )}
    </div>
  );
};

export default ScriptLine;